import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspaceStore } from "ui";
import { FFEScheduleInterface, Permissions } from "ts-interfaces";
import {
  archiveFFESchedule,
  createNewFFESchedule,
  saveFFESchedule,
} from "../../../v2/apis/ffeSchedulesApi";
import { useMattoState } from "../../../MattoState";
import { sendExceptionToSentry } from "../../../libs/sentry";
import {
  useProjectSchedulesStore,
  ProjectSchedulesEntry,
} from "./projectSchedulesStore";
import { runUndoable } from "./undoSnackbar";
import { useTeamScope } from "../../../hooks/useTeamScope";

const EMPTY_ENTRY: ProjectSchedulesEntry = {
  schedules: [],
  loading: true,
  loadError: false,
};

/**
 * Create-a-schedule action alone — no fetch, no store subscription — so a
 * create-only consumer (the overview's "Create new" menu) doesn't pay for a
 * schedules read or re-render when the list changes.
 */
export const useScheduleCreator = (
  projectId: string | undefined,
  uid: string | undefined,
) => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);

  const createSchedule = useCallback(async () => {
    if (!projectId || !uid || creating) return;
    setCreating(true);
    try {
      // createNewFFESchedule stamps uid + projectId + design_firm_id.
      const draft = createNewFFESchedule(uid, projectId);
      await saveFFESchedule(draft);
      navigate(`/home/schedules/${draft.id}`);
    } catch (e: any) {
      useMattoState
        .getState()
        .setSnackbarMessage(e?.message || "Couldn't create the schedule.");
      sendExceptionToSentry({
        error: e,
        contextName: "ffeScheduleCreate",
        context: { projectId },
      });
    } finally {
      setCreating(false);
    }
  }, [projectId, uid, creating, navigate]);

  return { createSchedule, creating };
};

/**
 * This project's FF&E schedules, backed by the shared projectSchedulesStore so
 * every overview consumer reads one fetch and sees the same archive.
 */
export const useProjectSchedules = (
  projectId: string | undefined,
  uid: string | undefined,
  permissions: Permissions | null,
) => {
  const entry = useProjectSchedulesStore(
    (s) => (projectId ? s.byProject[projectId] : undefined) ?? EMPTY_ENTRY,
  );
  const { createSchedule, creating } = useScheduleCreator(projectId, uid);
  // Re-fetch when the active workspace flips (personal <-> team), or when the
  // team's sharingV2 flag lands.
  const activeIdByUid = useWorkspaceStore((s) => s.activeIdByUid);
  const teamScope = useTeamScope(uid);

  const refresh = useCallback(
    () => useProjectSchedulesStore.getState().load(projectId, uid, permissions),
    [projectId, uid, permissions],
  );

  useEffect(() => {
    refresh();
  }, [refresh, activeIdByUid, teamScope]);

  // Archiving is reversible in the data model but there is no archived-schedules
  // view anywhere, so the Undo action is the only way back (#3792).
  const archiveSchedule = useCallback(
    async (schedule: FFEScheduleInterface) =>
      runUndoable({
        act: async () => {
          await archiveFFESchedule(schedule, permissions);
          // Shared store, so the row AND the sidebar's count both update.
          if (projectId)
            useProjectSchedulesStore
              .getState()
              .removeLocal(projectId, schedule.id);
        },
        undo: async () => {
          await archiveFFESchedule(schedule, permissions, false);
          if (projectId)
            useProjectSchedulesStore
              .getState()
              .upsertLocal(projectId, schedule);
        },
        done: "Schedule archived",
        undone: "Schedule restored",
        failed: "Couldn't archive the schedule.",
      }),
    [permissions, projectId],
  );

  return {
    schedules: entry.schedules,
    loading: entry.loading,
    loadError: entry.loadError,
    creating,
    createSchedule,
    archiveSchedule,
    refresh,
  };
};
